import { makeAutoObservable } from 'mobx';
import { graphqlClient, TGraphQLSubscriptions } from '../../api/graphql-api-client/graphql-api-client';
import * as queries from '../../graphql/queries';
import ErrorBoundaryStore from '../error-boundary-store/error-boundary-store';
import * as subscriptions from '../../graphql/subscriptions';
import {
  CreateChatMessagesMutation,
  ListChatMessagesQuery,
} from '../../graphql/API';
import * as mutations from '../../graphql/mutations';
import { MESSAGES_QUERY_LIMIT, MESSAGES_SORT_DIRECTION } from '../../constans/chat-messages-constants';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { localStoragePageInfo } from '../../utils/services/local-storage-service';
import { getErrorBoundaryStore } from '../error-boundary-store/error-boundary-store-instanse';
import PodiumInfoStore from '../podium-info-store/podium-info-store';
import { getPodiumInfoStore } from '../podium-info-store/podium-info-store-instance';
import { IGraphQLErrorResponse } from '../../utils/services/error-parsing-service';
import { BadgeNotificationService } from '../../utils/services/badge-notification-service';

export interface IChatMessage {
  chatMessageId: string;
  presentationId: string;
  senderName: string;
  createdAt: string;
  message: string;
}

type TNotification = Pick<IChatMessage, 'senderName' | 'createdAt' | 'message'> | null;

class ChatStore {
  private nextToken: string | null;
  private hashId: string;
  private isChatBoxVisible: boolean;
  private notificationBody: TNotification;
  private name: string;
  private chatMessages: IChatMessage[];
  private shouldTriggerScrollOnMyMessage: boolean;
  private myChatMessageId: string;
  private showNewMessageIndicator: boolean;
  private unreadChatMessagesNumber: number = 0;
  private badgeNotificationService: BadgeNotificationService;
  public messageText: string;
  public fieldValueName: string;

  constructor (
      private readonly podiumInfoStore: PodiumInfoStore = getPodiumInfoStore(),
      private readonly errorBoundary: ErrorBoundaryStore = getErrorBoundaryStore()
  ) {
    makeAutoObservable(this);
    this.badgeNotificationService = new BadgeNotificationService();
    this.chatMessages = [];
    this.notificationBody = null;
    this.isChatBoxVisible = false;
    this.name = '';
    this.messageText = '';
    this.fieldValueName = '';
    this.shouldTriggerScrollOnMyMessage = false;
    this.myChatMessageId = '';
  }

  public get userName (): string {
    return this.name;
  }

  public get isVisible (): boolean {
    return this.isChatBoxVisible;
  }

  public get chatMessagesList ():IChatMessage[] {
    return [...this.chatMessages].reverse();
  }

  public get notification (): TNotification {
    return this.notificationBody;
  }

  public get isVisibleNewMessageIndicator (): boolean {
    return this.showNewMessageIndicator;
  }

  public getUnreadMessagesNumber (): number {
    return this.unreadChatMessagesNumber;
  }

  public incrementUnreadMessagesNumber = (): void => {
    this.unreadChatMessagesNumber++;
    this.badgeNotificationService.setNotificationNumber(this.unreadChatMessagesNumber);
  };

  public resetUnreadMessagesNumber = (): void => {
    this.unreadChatMessagesNumber = 0;
    this.badgeNotificationService.setNotificationNumber(this.unreadChatMessagesNumber);
  };

  public defineNewMessageIndicatorVisibilityState = (isChatScrolledToBottom: boolean): void => {
    this.showNewMessageIndicator = !isChatScrolledToBottom && this.isChatBoxVisible;
  };

  public toggleChatBoxVisibility = (): void => {
    this.isChatBoxVisible = !this.isChatBoxVisible;
    if(this.isChatBoxVisible) this.clearNotificationBody();
  };

  public closeNotificationPopup = (): void => {
    this.clearNotificationBody();
  };

  public isMyMessage = (): boolean => {
    const chatMessagesLength = this.chatMessages.length;

    if(!chatMessagesLength) return false;
    return this.shouldTriggerScrollOnMyMessage;
  };

  public setHashId = (hashId: string): void => {
    this.hashId = hashId;
  };

  public setUserNameHandel = (name: string): void => {
    this.fieldValueName = name;
  };

  public setMessageTextHandel = (message: string): void => {
    this.messageText = message;
  };

  private getListChatMessagesItems = async (presentationId: string): Promise<Array<IChatMessage> | void> => {
    if (!presentationId) return;

    const result = await graphqlClient.operation<ListChatMessagesQuery>({
      query: queries.listChatMessages,
      variables: {
        presentationId: presentationId,
        limit: MESSAGES_QUERY_LIMIT,
        sortDirection: MESSAGES_SORT_DIRECTION,
        nextToken: this.nextToken
      }
    });
    // TODO: Need to fix graphql response type
    // @ts-ignore
    const { data: { listChatMessages: { items, nextToken }}} = result;
    this.nextToken = nextToken ? nextToken : '';

    return items;
  };

  public getChatMessages = async (): Promise<void> => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    if (!presentationId) return;

    this.getUserName(this.hashId);
    try {
      this.chatMessages = await this.getListChatMessagesItems(presentationId) || [];
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public getPaginateChatMessages = async (): Promise<void> => {
    if(!this.nextToken) return;

    const presentationId = this.podiumInfoStore.getPresentationId();

    if (!presentationId) return;

    try {
      const items = await this.getListChatMessagesItems(presentationId) || [];

      if (items.length) {
        this.chatMessages = [...this.chatMessages, ...items];
      }
    } catch (error) {
      this.errorBoundary.setGraphQLErrorMessage(error as IGraphQLErrorResponse);
    }
  };

  public onCreateChatMessagesSubscriptions = ():TGraphQLSubscriptions => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return graphqlClient.subscriptions({
      query: subscriptions.onCreateChatMessages,
      variables: {
        presentationId: presentationId,
      }
    }, this.onCreateChatMessagesCallback, () => this.onCreateChatMessagesSubscriptions());
  };

  private onCreateChatMessagesCallback = (data: { onCreateChatMessages:IChatMessage }):void => {
    const { onCreateChatMessages: message } = data;
    this.shouldTriggerScrollOnMyMessage = message.chatMessageId === this.myChatMessageId;
    this.updateMessagesOnSubscription(message);
  };

  public sendMessageHandler = (): void => {
    const message: IChatMessage = this.generateChatMessageDTO();
    this.setUserName(this.hashId);
    this.myChatMessageId = message.chatMessageId;
    this.messageText = '';
    this.createChatMessage(message);
  };

  public clearDataAboutLastUserMessage = (): void => {
    this.myChatMessageId = '';
    this.shouldTriggerScrollOnMyMessage = false;
  };

  private setUserName = (id: string): void => {
    if(this.name === this.fieldValueName) return;
    this.name = this.fieldValueName;
    localStoragePageInfo.setStorageProperty(id, 'userChatName', this.fieldValueName);
  };

  private getUserName = (id: string): void => {
    const chatNameLS = localStoragePageInfo.getStorageProperty(id, 'userChatName');
    if(chatNameLS) {
      this.name = chatNameLS;
    }
  };

  private createChatMessage = async (message: IChatMessage): Promise<void> => {
    try {
      await graphqlClient.operation<CreateChatMessagesMutation>({
        query: mutations.createChatMessages,
        variables: {
          input: message
        }
      });
    } catch (e) {
      this.errorBoundary.setGraphQLErrorMessage(e as IGraphQLErrorResponse);
    }
  };

  private generateChatMessageDTO = (): IChatMessage => {
    const presentationId = this.podiumInfoStore.getPresentationId();
    return {
      chatMessageId: uuidv4(),
      presentationId: presentationId || '',
      senderName: this.fieldValueName,
      createdAt: moment().toISOString(),
      message: this.messageText
    };
  };

  private setMessage = (message: IChatMessage): void => {
    this.chatMessages.unshift(message);
  };

  private updateMessagesOnSubscription = (payload: IChatMessage): void => {
    this.sendNotification(payload);
    this.setMessage(payload);
    this.updateUnreadMessagesNumber();
  };

  private sendNotification = ({ createdAt, message, senderName }: IChatMessage): void => {
    if(this.isChatBoxVisible) return;

    this.notificationBody = { createdAt, message, senderName };
  };

  private clearNotificationBody = (): void => {
    this.notificationBody = null;
  };

  private updateUnreadMessagesNumber = () => {
    if(this.isChatBoxVisible) return;
    this.incrementUnreadMessagesNumber();
  };
}

export type TChatStore = typeof ChatStore;
export default ChatStore;
